/**
 * Divide os CSVs de importação de grupos em lotes para os endpoints `.../import/batches` da API. Cada linha leva o
 * número que tem no arquivo, para o relatório de rejeitadas apontar a linha certa, e linhas que a API precisa comparar
 * entre si (mesmo CNPJ, mesmo ID de grupo ou mesmo código) vão no mesmo lote: assim a checagem de repetidos da API vale
 * para o arquivo inteiro. Mesmas regras de leitura da API (CustomerGroupCsvParser): BOM, quebra de linha \n ou \r\n,
 * separador vírgula ou ponto e vírgula (pelo cabeçalho), aspas e cabeçalho sem diferenciar maiúsculas nem "_".
 */

/** Linhas por lote. A API aceita até 2000 (CsvImportBatchDtoValidator.MaxLinesPerBatch). */
export const CSV_IMPORT_BATCH_SIZE = 500;

export interface CsvImportLine {
  line: number;
  text: string;
}

export interface CsvImportPlan {
  header: string;
  batches: CsvImportLine[][];
  /** Linhas de dados não vazias do arquivo. */
  totalLines: number;
}

/** Plano da importação de grupos: só a primeira linha de cada grupo distinto vai para a API. */
export interface CustomerGroupImportPlan extends CsvImportPlan {
  /** Linhas enviadas (uma por grupo distinto); as demais só repetem um grupo já visto. */
  sentLines: number;
  /** IDs do Magento que aparecem no arquivo, para calcular os grupos fora do arquivo. */
  magentoGroupIds: number[];
}

/** Divide a linha respeitando valores entre aspas ("" dentro de aspas vira "). Igual ao SplitLine da API. */
function splitCsvLine(line: string, separator: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const character = line[i];
    if (inQuotes) {
      if (character === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (character === '"') {
        inQuotes = false;
      } else {
        current += character;
      }
    } else if (character === '"') {
      inQuotes = true;
    } else if (character === separator) {
      fields.push(current);
      current = "";
    } else {
      current += character;
    }
  }
  fields.push(current);
  return fields;
}

const normalizeSpaces = (value: string) => value.trim().replace(/\s+/g, " ");
const normalizeHeader = (column: string) => normalizeSpaces(column.replace(/_/g, " ")).toLowerCase();

interface ParsedCsv {
  header: string;
  columns: string[];
  rows: { line: CsvImportLine; fields: string[] }[];
}

function readCsv(content: string): ParsedCsv | null {
  const lines = content.replace(/^\uFEFF/, "").split("\n").map((line) => line.replace(/\r$/, ""));
  const headerIndex = lines.findIndex((line) => line.trim() !== "");
  if (headerIndex < 0) return null;

  const header = lines[headerIndex];
  const separator = header.includes(";") ? ";" : ",";
  const rows: ParsedCsv["rows"] = [];
  lines.forEach((text, index) => {
    if (index <= headerIndex || text.trim() === "") return;
    rows.push({ line: { line: index + 1, text }, fields: splitCsvLine(text, separator) });
  });
  return { header, columns: splitCsvLine(header, separator).map(normalizeHeader), rows };
}

/**
 * Monta os lotes sem separar linhas que compartilham alguma chave (union-find). Os grupos de linhas seguem a ordem
 * da primeira linha de cada um.
 */
function batchByKeys(lines: CsvImportLine[], keysOf: (index: number) => string[], batchSize: number): CsvImportLine[][] {
  const parent = lines.map((_, index) => index);
  const find = (index: number): number => {
    while (parent[index] !== index) {
      parent[index] = parent[parent[index]];
      index = parent[index];
    }
    return index;
  };
  const firstByKey = new Map<string, number>();
  lines.forEach((_, index) => {
    for (const key of keysOf(index)) {
      const first = firstByKey.get(key);
      if (first === undefined) firstByKey.set(key, index);
      else parent[find(index)] = find(first);
    }
  });

  const groups = new Map<number, CsvImportLine[]>();
  lines.forEach((line, index) => {
    const root = find(index);
    const group = groups.get(root);
    if (group) group.push(line);
    else groups.set(root, [line]);
  });

  const batches: CsvImportLine[][] = [];
  let current: CsvImportLine[] = [];
  for (const group of groups.values()) {
    if (current.length > 0 && current.length + group.length > batchSize) {
      batches.push(current);
      current = [];
    }
    current.push(...group);
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/**
 * CSV de clientes com grupo (export de clientes do Magento): todas as linhas são enviadas, e as de um mesmo CNPJ vão
 * no mesmo lote. null quando o arquivo não tem nenhuma linha (nem cabeçalho).
 */
export function planClientGroupImport(content: string, batchSize = CSV_IMPORT_BATCH_SIZE): CsvImportPlan | null {
  const csv = readCsv(content);
  if (!csv) return null;
  const cnpjIndex = csv.columns.indexOf("cnpj");
  const lines = csv.rows.map((row) => row.line);
  const batches = batchByKeys(
    lines,
    (index) => {
      const digits = cnpjIndex >= 0 ? (csv.rows[index].fields[cnpjIndex] ?? "").replace(/\D/g, "") : "";
      // Sem CNPJ a linha vai sozinha; a API rejeita com o motivo.
      return digits.length > 0 ? [digits] : [];
    },
    batchSize,
  );
  return { header: csv.header, batches, totalLines: lines.length };
}

/**
 * CSV de grupos: magento_group_id,code,name ou o export de clientes do Magento (Customer Group ID e Customer Group
 * Description), em que cada grupo se repete em muitas linhas. Só vai para a API a primeira linha de cada grupo
 * distinto (ID + código + nome); variantes do mesmo ID ou do mesmo código vão no mesmo lote, para a API apontar o
 * conflito. null quando o arquivo não tem nenhuma linha (nem cabeçalho).
 */
export function planCustomerGroupImport(content: string, batchSize = CSV_IMPORT_BATCH_SIZE): CustomerGroupImportPlan | null {
  const csv = readCsv(content);
  if (!csv) return null;
  const idIndex = csv.columns.findIndex((column) => column === "magento group id" || column === "customer group id");
  const codeIndex = csv.columns.findIndex((column) => column === "code" || column === "customer group description");
  const nameIndex = csv.columns.indexOf("name");
  const field = (fields: string[], index: number) => (index >= 0 ? normalizeSpaces(fields[index] ?? "") : "");

  const sent: { line: CsvImportLine; id: string; code: string }[] = [];
  const seen = new Set<string>();
  const magentoGroupIds = new Set<number>();
  for (const { line, fields } of csv.rows) {
    const id = field(fields, idIndex);
    const code = field(fields, codeIndex).toLowerCase();
    if (/^\d+$/.test(id)) magentoGroupIds.add(Number(id));
    // Sem as colunas, cada linha vai como veio: a API recusa o cabeçalho.
    const key = idIndex >= 0 && codeIndex >= 0 ? `${id}|${code}|${field(fields, nameIndex)}` : `#${line.line}`;
    if (seen.has(key)) continue;
    seen.add(key);
    sent.push({ line, id, code });
  }

  const batches = batchByKeys(
    sent.map((entry) => entry.line),
    (index) => {
      const { id, code } = sent[index];
      return [id ? `id:${id}` : null, code ? `code:${code}` : null].filter((key): key is string => key !== null);
    },
    batchSize,
  );
  return {
    header: csv.header,
    batches,
    totalLines: csv.rows.length,
    sentLines: sent.length,
    magentoGroupIds: [...magentoGroupIds],
  };
}
